import { AppColorVariant } from "../theme/AppColorVariant";
import { MigrationStatus } from "./MigrationStatus";
import { ConflictReason } from "./ConflictReason";
import { MigrationFlagStatus } from "./Flags/MigrationFlagStatus";
import { ConflictResolutionAction } from "./ConflictResolutionAction";

/** A single file competing for a target path in a conflict group. Mirrors the backend `ConflictGroupFileDto`. */
export interface ConflictGroupFileDto {
  fileId: string;
  filePath: string;
  filename: string;
  status: MigrationStatus;
  /** Size (MB) of the local file — shown against the SharePoint occupant's size for size/hash mismatches. */
  fileSizeInMegabytes: number;
  conflictReason: ConflictReason | null;
  conflictStatus: MigrationFlagStatus | null;
  /** The action this conflict was resolved with (so the drawer can pre-select it); null while unresolved. */
  conflictResolution: ConflictResolutionAction | null;
  /** For a "Rename & keep both" or "Accept SharePoint copy" copy held until its group's winner uploads: the winner's source path. Absent when it waits for nothing or the winner was deleted. */
  waitingForFilePath?: string | null;
  /** Where the winner a held copy waits for stands. Absent when the copy waits for nothing. */
  waitingForState?: ConflictWaitingForState | null;
}

/**
 * Where the winner a held "Rename & keep both" or "Accept SharePoint copy" copy waits for stands. Mirrors the backend
 * `ConflictWaitingForState`. Only `ToUpload` ends without anyone acting: a `NotFound` winner has to be retried, and
 * every other state needs the group resolved again.
 */
export enum ConflictWaitingForState {
  ToUpload = "toUpload",
  InSharePoint = "inSharePoint",
  NotFound = "notFound",
  NoLongerWinner = "noLongerWinner",
  Deleted = "deleted",
}

/** Muted while the winner is still to upload; amber when the copy stays held until someone acts. */
export const ConflictWaitingForStateColors: Record<ConflictWaitingForState, AppColorVariant> = {
  [ConflictWaitingForState.ToUpload]: "neutral",
  [ConflictWaitingForState.InSharePoint]: "warning",
  [ConflictWaitingForState.NotFound]: "warning",
  [ConflictWaitingForState.NoLongerWinner]: "warning",
  [ConflictWaitingForState.Deleted]: "warning",
};

/**
 * The line the drawer shows on a held copy, whether it waits to upload under a new name or to adopt the winner's file.
 * For a winner that was not found it leads with the retry, because a renamed copy released first takes the plain name
 * and a winner retried later would overwrite it.
 *
 * `acceptNeedsAWinner` marks an accepting copy whose group has nothing else to adopt. Such a copy cannot be released:
 * without a winner, groupResolveState marks its group missingWinner and the card does not send it. So once its winner
 * was not found or deleted, it is pointed at a new winner (which it then waits for) or another action, not at resolving
 * the group as it stands.
 */
export const conflictWaitingMessage = (
  state: ConflictWaitingForState,
  winnerPath: string | null | undefined,
  acceptNeedsAWinner = false
): string => {
  const winner = winnerPath ?? "the winner";
  switch (state) {
    case ConflictWaitingForState.ToUpload:
      return `Waiting for ${winner} to upload`;
    case ConflictWaitingForState.NotFound:
      return acceptNeedsAWinner
        ? `Waiting for ${winner}: not found. Retry it; if that file is gone for good, choose a new winner or give this copy another action.`
        : `Waiting for ${winner}: not found. Retry it; resolve the group to release this copy only if that file is gone for good.`;
    case ConflictWaitingForState.InSharePoint:
      return `Waiting for ${winner}: already in SharePoint. Resolve the group to release this copy.`;
    case ConflictWaitingForState.NoLongerWinner:
      return `Waiting for ${winner}: no longer the winner. Resolve the group to release this copy.`;
    case ConflictWaitingForState.Deleted:
      return acceptNeedsAWinner
        ? "Waiting for a copy that was deleted. Choose a new winner or give this copy another action."
        : "Waiting for a copy that was deleted. Resolve the group to release this copy.";
  }
};

/** One target path claimed by N files, resolved as a whole group. Mirrors the backend `ConflictGroupDto`. */
export interface ConflictGroupDto {
  targetPath: string;
  /** True when a file has already migrated to this target path, so the group needs no winner (every file can Accept-adopt the occupant). */
  hasMigratedOccupant: boolean;
  /** Source path of the migrated file occupying this target path (what "Accept SharePoint copy" adopts); null when there is none. */
  occupantFilePath: string | null;
  /** Size (MB) of the migrated occupant, shown alongside its source path; null when there is none. */
  occupantSizeInMegabytes: number | null;
  files: ConflictGroupFileDto[];
}

/** POST body item for `ResolveConflictGroup`. */
export interface ConflictResolutionItemDto {
  fileId: string;
  action: ConflictResolutionAction;
}
